import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag, Trash2, ChevronLeft, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { createCheckout } from "../api";

const emptyForm = { name: "", email: "", phone: "", address: "" };

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("cart"); // cart | checkout
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill known details for logged-in customers (faster checkout)
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const close = () => setIsOpen(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const goToPayment = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.address) {
      toast.error("Please complete name, email and address");
      return;
    }
    setSubmitting(true);
    try {
      const { checkout_url } = await createCheckout({
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        customer: form,
        origin_url: window.location.origin
      });
      if (checkout_url) {
        window.location.href = checkout_url; // redirect to Stripe hosted checkout
      } else {
        throw new Error("no url");
      }
    } catch (err) {
      toast.error("Could not start payment", { description: "Please try again in a moment." });
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[70] ${isOpen ? "" : "pointer-events-none"}`}>
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-400 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={close}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-[440px] bg-[#141210] border-l border-[#2c2822] flex flex-col transition-transform duration-500 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#2c2822]">
          <div className="flex items-center gap-3">
            {step === "checkout" && (
              <button onClick={() => setStep("cart")} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors">
                <ChevronLeft size={20} />
              </button>
            )}
            <h3 className="font-display text-2xl text-[#f4efe4]">
              {step === "cart" ? (
                <>Your Cart <span className="text-[#c9a24b]">({items.length})</span></>
              ) : (
                "Checkout"
              )}
            </h3>
          </div>
          <button onClick={close} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors">
            <X size={22} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <div className="w-16 h-16 rounded-full border border-[#2c2822] flex items-center justify-center mb-5">
              <ShoppingBag size={26} className="text-[#c9a24b]" />
            </div>
            <p className="text-[#f4efe4] text-lg mb-2">Your cart is empty</p>
            <p className="text-[#a29a8c] text-sm mb-6">Discover our ritual of natural care.</p>
            <Link to="/shop" onClick={close} className="btn-outline-gold px-8 py-3 rounded-full text-sm tracking-[0.15em] uppercase">
              Shop the Collection
            </Link>
          </div>
        ) : step === "checkout" ? (
          /* CHECKOUT FORM */
          <form onSubmit={goToPayment} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {user ? (
                <div className="flex items-center gap-2 rounded-md border border-[#c9a24b]/30 bg-[#c9a24b]/5 px-4 py-2.5" data-testid="checkout-signedin">
                  <Lock size={13} className="text-[#c9a24b]" />
                  <p className="text-[#c8c0b2] text-xs">Signed in as <span className="text-[#f4efe4]">{user.email}</span> — details pre-filled.</p>
                </div>
              ) : (
                <div className="rounded-md border border-[#2c2822] bg-[#0b0a09] px-4 py-2.5" data-testid="checkout-guest-hint">
                  <p className="text-[#a29a8c] text-xs">
                    Checking out as a guest.{" "}
                    <Link to="/login" onClick={close} className="text-[#c9a24b] hover:underline">Sign in</Link> to check out faster.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-[#a29a8c] text-xs tracking-[0.15em] uppercase mb-2">Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} className="w-full bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="block text-[#a29a8c] text-xs tracking-[0.15em] uppercase mb-2">Email *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors" placeholder="you@email.com" />
              </div>
              <div>
                <label className="block text-[#a29a8c] text-xs tracking-[0.15em] uppercase mb-2">Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="w-full bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors" placeholder="Optional" />
              </div>
              <div>
                <label className="block text-[#a29a8c] text-xs tracking-[0.15em] uppercase mb-2">Shipping Address *</label>
                <textarea name="address" value={form.address} onChange={handleChange} rows={3} className="w-full bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors resize-none" placeholder="Street, City, State, ZIP, Country" />
              </div>
              <div className="rounded-md border border-[#2c2822] bg-[#0b0a09] px-4 py-3">
                <p className="text-[#6b6459] text-xs tracking-[0.12em] uppercase mb-2">Shipping</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between text-[#c8c0b2]"><span>USPS Ground Advantage <span className="text-[#6b6459]">· 2–5 days</span></span><span className="text-[#c9a24b]">$6.99</span></li>
                  <li className="flex justify-between text-[#c8c0b2]"><span>USPS Priority Mail Flat Rate <span className="text-[#6b6459]">· 1–3 days</span></span><span className="text-[#c9a24b]">$9.99</span></li>
                  <li className="flex justify-between text-[#c8c0b2]"><span>UPS Ground <span className="text-[#6b6459]">· 1–3 days</span></span><span className="text-[#c9a24b]">$11.99</span></li>
                  <li className="flex justify-between text-[#c8c0b2]"><span>FedEx Ground Economy <span className="text-[#6b6459]">· 2–7 days</span></span><span className="text-[#c9a24b]">$10.99</span></li>
                </ul>
                <p className="text-[#6b6459] text-[11px] mt-2">Choose your carrier on the secure payment step.</p>
              </div>
              <div className="rounded-md border border-[#2c2822] bg-[#0b0a09] px-4 py-3">
                <p className="text-[#6b6459] text-xs tracking-[0.12em] uppercase mb-2">Pay With</p>
                <div className="flex flex-wrap gap-2">
                  {["Card", "Klarna", "Affirm"].map((m) => (
                    <span key={m} className="text-[#c9a24b] border border-[#c9a24b]/40 rounded-full px-3 py-1 text-xs">{m}</span>
                  ))}
                </div>
                <p className="text-[#6b6459] text-[11px] mt-2">Klarna & Affirm appear when your order qualifies.</p>
              </div>
            </div>
            <div className="border-t border-[#2c2822] px-6 py-5 space-y-4">
              <div className="flex justify-between text-[#f4efe4]">
                <span className="text-sm tracking-wide">Total</span>
                <span className="font-display text-xl text-[#c9a24b]">${subtotal.toFixed(2)}</span>
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full py-4 rounded-full text-sm tracking-[0.2em] uppercase font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <Lock size={15} /> {submitting ? "Redirecting..." : "Continue to Payment"}
              </button>
              <p className="text-[#6b6459] text-[11px] text-center">Secure payment powered by Stripe.</p>
            </div>
          </form>
        ) : (
          /* CART LIST */
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-contain p-1.5 rounded-md border border-[#2c2822] bg-gradient-to-b from-[#f6f1e7] to-[#e4dccb]" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <Link to={`/product/${item.slug}`} onClick={close} className="text-[#f4efe4] text-sm hover:text-[#c9a24b] transition-colors leading-snug">
                        {item.name}
                      </Link>
                      <button onClick={() => removeItem(item.id)} className="text-[#6b6459] hover:text-[#c9a24b] transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="text-[#c9a24b] text-sm mt-1">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 border border-[#2c2822] rounded-full flex items-center justify-center text-[#f4efe4] hover:border-[#c9a24b] transition-colors">
                        <Minus size={13} />
                      </button>
                      <span className="text-[#f4efe4] text-sm w-5 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-7 h-7 border border-[#2c2822] rounded-full flex items-center justify-center text-[#f4efe4] hover:border-[#c9a24b] transition-colors">
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={clearCart} className="text-[#6b6459] text-xs tracking-wide hover:text-[#c9a24b] transition-colors">
                Clear cart
              </button>
            </div>

            <div className="border-t border-[#2c2822] px-6 py-5 space-y-4">
              <div className="flex justify-between text-[#f4efe4]">
                <span className="text-sm tracking-wide">Subtotal</span>
                <span className="font-display text-xl text-[#c9a24b]">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-[#6b6459] text-xs">Shipping & taxes calculated at checkout.</p>
              <button onClick={() => setStep("checkout")} className="btn-gold w-full py-4 rounded-full text-sm tracking-[0.2em] uppercase font-medium">
                Checkout
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
